import React, { useState, useEffect } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Slider,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  LocalOffer as TagIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Diamond as DiamondIcon,
  WorkspacePremium as PremiumIcon,
  MilitaryTech as RankIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Speed as SpeedIcon,
  Percent as PercentIcon,
  Timer as TimerIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ColorLens as ColorIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = '/api/admin';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: { xs: 1.5, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const VipRanksManagement = () => {
  const [tabValue, setTabValue] = usePersistedTab('vipranks', 0);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // VIP статусы (за инвестиции/депозиты) - загружаются с сервера (10 уровней)
  const [vipStatuses, setVipStatuses] = useState([]);

  // Ранги (за регулярность claim'ов - стрики) - 17 рангов
  const [ranks, setRanks] = useState([]);

  // Настройки системы
  const [systemSettings, setSystemSettings] = useState({
    vipAutoAssign: true,
    vipManualOverride: false,
    vipShowBadge: true,
    vipNotifyOnUpgrade: true,
    vipRetroactiveRewards: false,
    rankAutoProgress: true,
    rankShowProgress: true,
    rankNotifyOnUpgrade: true,
    rankRetroactiveRewards: false,
    rankResetOnWithdraw: false
  });

  // Статистика
  const [statistics, setStatistics] = useState({
    totalVipUsers: 0,
    vipDistribution: [], // Массив {vip_level, count} для всех уровней
    averageRank: 0,
    rankDistribution: [],
    totalRankRewards: 0,
    totalVipBonuses: 0
  });

  const [editingVip, setEditingVip] = useState(null);
  const [editingRank, setEditingRank] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');

      // Загружаем настройки VIP
      const vipResponse = await axios.get(`${API_URL}/vip-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Загружаем настройки рангов
      const ranksResponse = await axios.get(`${API_URL}/rank-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Парсим настройки и заполняем состояния
      parseSettings(vipResponse.data.data, ranksResponse.data.data);

      // Загружаем статистику
      await loadStatistics();
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const parseSettings = (vipSettings, rankSettings) => {
    // Парсим VIP настройки (массив из 10 уровней)
    if (vipSettings && Array.isArray(vipSettings)) {
      const vipData = vipSettings.map(vip => ({
        id: vip.level,
        level: vip.level,
        name: vip.name || `VIP ${vip.level}`,
        threshold: parseFloat(vip.threshold) || 0,
        aprBonus: parseFloat(vip.apr_bonus) || 0,
        color: getVipColor(vip.level),
        icon: getVipIcon(vip.level),
        active: true
      }));

      if (vipData.length > 0) setVipStatuses(vipData);
    }

    // Парсим настройки рангов
    if (rankSettings && Array.isArray(rankSettings)) {
      const rankData = rankSettings.map(rank => ({
        id: rank.level,
        level: rank.level,
        name: rank.name || `Ранг ${rank.level}`,
        daysRequired: parseInt(rank.days) || 0,
        aprBonus: parseFloat(rank.apr_bonus) || 0,
        couponMultiplier: rank.coupon_multiplier ? parseFloat(rank.coupon_multiplier) : 0,
        couponDuration: rank.coupon_duration ? parseInt(rank.coupon_duration) : 0,
        color: getRankColor(rank.level),
        icon: getRankIcon(rank.level)
      }));

      if (rankData.length > 0) setRanks(rankData);
    }
  };

  const getVipColor = (level) => {
    // Цвета для 10 VIP уровней (градиент от базового к элитному)
    const colors = [
      '#9CA3AF',  // 1: Starter - серый
      '#CD7F32',  // 2: Bronze - бронзовый
      '#A78BFA',  // 3: Warrior - фиолетовый
      '#C0C0C0',  // 4: Silver - серебряный
      '#60A5FA',  // 5: Elite - синий
      '#FFD700',  // 6: Gold - золотой
      '#F59E0B',  // 7: Diamond - янтарный
      '#E5E4E2',  // 8: Platinum - платиновый
      '#C084FC',  // 9: Royal - королевский фиолетовый
      '#8B5CF6'   // 10: Cosmic - космический фиолетовый
    ];
    return colors[level - 1] || '#6366f1';
  };

  const getVipIcon = (level) => {
    // Иконки для 10 VIP уровней - берем из названий в БД
    const icons = [
      '🌱',  // 1: Starter
      '🥉',  // 2: Bronze
      '⚔️',  // 3: Warrior
      '🥈',  // 4: Silver
      '⭐',  // 5: Elite
      '🥇',  // 6: Gold
      '💠',  // 7: Diamond
      '💎',  // 8: Platinum
      '👑',  // 9: Royal
      '🌌'   // 10: Cosmic
    ];
    return icons[level - 1] || '⭐';
  };

  const getRankColor = (level) => {
    const colors = [
      '#9CA3AF', '#A78BFA', '#60A5FA', '#34D399', '#FBBF24', '#F59E0B', '#CD7F32', '#C0C0C0',
      '#FFD700', '#E879F9', '#A78BFA', '#818CF8', '#60A5FA', '#3B82F6', '#8B5CF6', '#7C3AED', '#6D28D9'
    ];
    return colors[level - 1] || '#6366f1';
  };

  const getRankIcon = (level) => {
    const icons = [
      '🌱', '📚', '⚔️', '🛡️', '🎖️', '🏅', '🥉', '🥈',
      '🏆', '🌟', '🔮', '⚡', '👑', '✨', '🔱', '💫', '🌌'
    ];
    return icons[level - 1] || '⭐';
  };

  const loadStatistics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/vip-ranks-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        const stats = response.data.data;
        setStatistics({
          totalVipUsers: stats.vipStats?.reduce((acc, v) => acc + parseInt(v.count || 0), 0) || 0,
          vipDistribution: stats.vipStats || [], // Сохраняем весь массив для динамического отображения
          averageRank: stats.rankStats?.reduce((acc, r) => acc + (parseFloat(r.avg_streak) || 0), 0) / (stats.rankStats?.length || 1) || 0,
          rankDistribution: stats.rankStats || [],
          totalRankRewards: stats.rankStats?.reduce((acc, r) => acc + parseInt(r.count || 0), 0) || 0,
          totalVipBonuses: stats.vipStats?.reduce((acc, v) => acc + parseFloat(v.total_deposits || 0), 0) || 0
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const saveVipStatus = async (vipStatus) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');

      // Конвертируем значения
      const aprBonus = typeof vipStatus.aprBonus === 'string' ? parseFloat(vipStatus.aprBonus) : vipStatus.aprBonus;

      // Обновляем локальное состояние
      const updatedVipStatuses = vipStatuses.map(v =>
        v.id === vipStatus.id ? {...vipStatus, aprBonus: aprBonus || 0} : v
      );

      // Отправляем весь массив VIP уровней на сервер
      const requestData = updatedVipStatuses.map(vip => ({
        level: vip.level,
        name: vip.name,
        threshold: parseFloat(vip.threshold) || 0,
        apr_bonus: parseFloat(vip.aprBonus) || 0
      }));

      await axios.put(`${API_URL}/vip-settings`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setVipStatuses(updatedVipStatuses);
      setEditingVip(null);
      showNotification('VIP статус сохранен', 'success');
    } catch (error) {
      console.error('Error saving VIP status:', error);
      showNotification(`Ошибка сохранения VIP статуса: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveRank = async (rank) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');

      // Конвертируем строковые значения в числа
      const aprBonus = typeof rank.aprBonus === 'string' ? parseFloat(rank.aprBonus) || 0 : rank.aprBonus;
      const couponMultiplier = typeof rank.couponMultiplier === 'string' ? parseFloat(rank.couponMultiplier) || 0 : rank.couponMultiplier;

      const requestData = {
        ranks: [{
          level: rank.level,
          name: rank.name,
          days: rank.daysRequired,
          apr_bonus: aprBonus,
          coupon_multiplier: couponMultiplier,
          coupon_duration: rank.couponDuration
        }]
      };

      await axios.put(`${API_URL}/rank-settings`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Обновляем локальное состояние с числовыми значениями
      setRanks(prev => prev.map(r => r.id === rank.id ? {
        ...rank,
        aprBonus: aprBonus,
        couponMultiplier: couponMultiplier
      } : r));
      setEditingRank(null);
      showNotification('Ранг сохранен', 'success');
    } catch (error) {
      console.error('Error saving rank:', error);
      showNotification('Ошибка сохранения ранга', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveSystemSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');

      const settingsData = {
        vip_auto_assign: systemSettings.vipAutoAssign,
        vip_manual_override: systemSettings.vipManualOverride,
        vip_show_badge: systemSettings.vipShowBadge,
        vip_notify_on_upgrade: systemSettings.vipNotifyOnUpgrade,
        rank_auto_progress: systemSettings.rankAutoProgress,
        rank_show_progress: systemSettings.rankShowProgress,
        rank_notify_on_upgrade: systemSettings.rankNotifyOnUpgrade
      };

      await axios.post(`${API_URL}/settings`, settingsData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showNotification('Системные настройки сохранены', 'success');
    } catch (error) {
      console.error('Error saving system settings:', error);
      showNotification('Ошибка сохранения настроек', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PremiumIcon sx={{ fontSize: 40, color: '#FFD700' }} />
          VIP статусы и Ранги
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Управление системой VIP статусов и рангов
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#FFD700', width: 56, height: 56 }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{formatNumber(statistics.totalVipUsers)}</Typography>
                  <Typography variant="body2" color="text.secondary">VIP пользователей</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#8B5CF6', width: 56, height: 56 }}>
                  <TrophyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{statistics.averageRank.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">Средний ранг</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#10B981', width: 56, height: 56 }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{formatNumber(statistics.totalVipBonuses)}</Typography>
                  <Typography variant="body2" color="text.secondary">VIP бонусов выдано</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#F59E0B', width: 56, height: 56 }}>
                  <DiamondIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{formatNumber(statistics.totalRankRewards)}</Typography>
                  <Typography variant="body2" color="text.secondary">Наград за ранги</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="VIP статусы" icon={<StarIcon />} iconPosition="start" />
          <Tab label="Ранги" icon={<RankIcon />} iconPosition="start" />
          <Tab label="Настройки системы" icon={<SettingsIcon />} iconPosition="start" />
        </Tabs>

        {/* VIP статусы */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              VIP статусы назначаются автоматически на основе общей суммы депозитов пользователя
            </Alert>

            <Grid container spacing={3}>
              {vipStatuses.map((vip) => (
                <Grid item xs={12} md={6} key={vip.id}>
                  <Card sx={{
                    border: `2px solid ${vip.color}`,
                    position: 'relative',
                    overflow: 'visible'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: -20,
                      left: 20,
                      bgcolor: vip.color,
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>{vip.icon}</span>
                      <Typography variant="h6">{vip.level}. {vip.name}</Typography>
                    </Box>

                    <CardContent sx={{ mt: 3 }}>
                      {editingVip === vip.id ? (
                        <Box>
                          <TextField
                            fullWidth
                            label="Порог депозитов"
                            type="text"
                            value={vip.threshold}
                            onChange={(e) => {
                              const value = e.target.value.replace(',', '.');
                              const parsed = parseFloat(value);
                              if (!isNaN(parsed) || value === '' || value === '.') {
                                setVipStatuses(prev =>
                                  prev.map(v => v.id === vip.id ? {...v, threshold: value === '' || value === '.' ? 0 : parsed} : v)
                                );
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">SPERM</InputAdornment>
                            }}
                            sx={{ mb: 2 }}
                            helperText="Можно использовать точку: 10000 или 0.5"
                          />

                          <TextField
                            fullWidth
                            label="Бонус к APR"
                            type="text"
                            value={vip.aprBonus}
                            onChange={(e) => {
                              const value = e.target.value.replace(',', '.');
                              if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                                setVipStatuses(prev =>
                                  prev.map(v => v.id === vip.id ? {...v, aprBonus: value} : v)
                                );
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            sx={{ mb: 2 }}
                            helperText="Десятичные числа через точку: 0.1, 0.05, 0.005"
                          />

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() => saveVipStatus(vip)}
                              disabled={loading}
                            >
                              Сохранить
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={() => setEditingVip(null)}
                            >
                              Отмена
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <List>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#6366f1' }}>
                                  <MoneyIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary="Порог депозитов"
                                secondary={`${formatNumber(vip.threshold)} SPERM`}
                              />
                            </ListItem>

                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#10B981' }}>
                                  <PercentIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary="Бонус к APR"
                                secondary={`+${vip.aprBonus}%`}
                              />
                            </ListItem>

                          </List>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            <Chip
                              label={vip.active ? 'Активен' : 'Неактивен'}
                              color={vip.active ? 'success' : 'default'}
                              size="small"
                            />
                            <IconButton onClick={() => setEditingVip(vip.id)} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Ранги */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Ранги повышаются автоматически за регулярность claim'ов (стрик дней подряд). За достижение ранга пользователь получает купон с бустом.
            </Alert>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Уровень</TableCell>
                    <TableCell>Название</TableCell>
                    <TableCell>Дней стрика</TableCell>
                    <TableCell>APR бонус</TableCell>
                    <TableCell>Буст купона</TableCell>
                    <TableCell>Длительность (сек)</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranks.map((rank) => (
                    <TableRow key={rank.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: rank.color, width: 32, height: 32 }}>
                            {rank.icon}
                          </Avatar>
                          <Typography>{rank.level}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <TextField
                            value={rank.name}
                            onChange={(e) => setRanks(prev =>
                              prev.map(r => r.id === rank.id ? {...r, name: e.target.value} : r)
                            )}
                            size="small"
                          />
                        ) : (
                          <Typography sx={{ fontWeight: 'bold', color: rank.color }}>
                            {rank.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <TextField
                            type="number"
                            value={rank.daysRequired}
                            onChange={(e) => setRanks(prev =>
                              prev.map(r => r.id === rank.id ? {...r, daysRequired: parseInt(e.target.value) || 0} : r)
                            )}
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">дней</InputAdornment>
                            }}
                          />
                        ) : (
                          `${rank.daysRequired} дней`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <TextField
                            type="text"
                            value={rank.aprBonus}
                            onChange={(e) => {
                              const value = e.target.value.replace(',', '.');
                              // Разрешаем только цифры и одну точку
                              if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                                setRanks(prev =>
                                  prev.map(r => r.id === rank.id ? {...r, aprBonus: value} : r)
                                );
                              }
                            }}
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            placeholder="0.03"
                          />
                        ) : (
                          `+${rank.aprBonus}%`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <TextField
                            type="text"
                            value={((rank.couponMultiplier - 1) * 100).toFixed(1)}
                            onChange={(e) => {
                              const value = e.target.value.replace(',', '.');
                              // Разрешаем только цифры и одну точку
                              if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                                // Конвертируем процент в множитель
                                const multiplier = (parseFloat(value) / 100) + 1;
                                setRanks(prev =>
                                  prev.map(r => r.id === rank.id ? {...r, couponMultiplier: multiplier} : r)
                                );
                              }
                            }}
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            placeholder="50"
                            helperText="Процент увеличения дохода"
                          />
                        ) : (
                          `+${((rank.couponMultiplier - 1) * 100).toFixed(0)}%`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <TextField
                            type="number"
                            value={rank.couponDuration}
                            onChange={(e) => setRanks(prev =>
                              prev.map(r => r.id === rank.id ? {...r, couponDuration: parseInt(e.target.value) || 0} : r)
                            )}
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">сек</InputAdornment>
                            }}
                          />
                        ) : (
                          `${rank.couponDuration} сек`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRank === rank.id ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              color="success"
                              onClick={() => saveRank(rank)}
                              disabled={loading}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => setEditingRank(null)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() => setEditingRank(rank.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* График прогресса рангов */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Прогрессия рангов (по дням стрика)
              </Typography>
              <Box sx={{ mt: 2 }}>
                {ranks.map((rank, index) => (
                  <Box key={rank.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{rank.icon} {rank.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rank.daysRequired} {rank.daysRequired === 1 ? 'день' : 'дней'} | APR +{rank.aprBonus}% | Буст +{((rank.couponMultiplier - 1) * 100).toFixed(0)}% на {rank.couponDuration}с
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(rank.daysRequired / 60) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: rank.color
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </TabPanel>

        {/* Настройки системы */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon color="primary" />
                  Настройки VIP
                </Typography>

                <List>
                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.vipAutoAssign}
                          onChange={(e) => setSystemSettings({...systemSettings, vipAutoAssign: e.target.checked})}
                        />
                      }
                      label="Автоматическое назначение VIP"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.vipManualOverride}
                          onChange={(e) => setSystemSettings({...systemSettings, vipManualOverride: e.target.checked})}
                        />
                      }
                      label="Разрешить ручное изменение VIP"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.vipShowBadge}
                          onChange={(e) => setSystemSettings({...systemSettings, vipShowBadge: e.target.checked})}
                        />
                      }
                      label="Показывать VIP бейдж"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.vipNotifyOnUpgrade}
                          onChange={(e) => setSystemSettings({...systemSettings, vipNotifyOnUpgrade: e.target.checked})}
                        />
                      }
                      label="Уведомлять о повышении VIP"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.vipRetroactiveRewards}
                          onChange={(e) => setSystemSettings({...systemSettings, vipRetroactiveRewards: e.target.checked})}
                        />
                      }
                      label="Ретроактивные награды VIP"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RankIcon color="primary" />
                  Настройки рангов
                </Typography>

                <List>
                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.rankAutoProgress}
                          onChange={(e) => setSystemSettings({...systemSettings, rankAutoProgress: e.target.checked})}
                        />
                      }
                      label="Автоматическое повышение рангов"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.rankShowProgress}
                          onChange={(e) => setSystemSettings({...systemSettings, rankShowProgress: e.target.checked})}
                        />
                      }
                      label="Показывать прогресс ранга"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.rankNotifyOnUpgrade}
                          onChange={(e) => setSystemSettings({...systemSettings, rankNotifyOnUpgrade: e.target.checked})}
                        />
                      }
                      label="Уведомлять о повышении ранга"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.rankRetroactiveRewards}
                          onChange={(e) => setSystemSettings({...systemSettings, rankRetroactiveRewards: e.target.checked})}
                        />
                      }
                      label="Ретроактивные награды за ранги"
                    />
                  </ListItem>

                  <ListItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.rankResetOnWithdraw}
                          onChange={(e) => setSystemSettings({...systemSettings, rankResetOnWithdraw: e.target.checked})}
                        />
                      }
                      label="Сбрасывать ранг при выводе"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={saveSystemSettings}
                  disabled={loading}
                >
                  Сохранить настройки системы
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Уведомления */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} onClose={() => setNotification({ ...notification, open: false })}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Диалог подтверждения */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
        <DialogTitle>Подтверждение действия</DialogTitle>
        <DialogContent>
          <Typography>Вы уверены, что хотите выполнить это действие?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (confirmDialog.action) confirmDialog.action();
              setConfirmDialog({ open: false, action: null });
            }}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0 }} />}
    </Container>
  );
};

export default VipRanksManagement;
